import React, { useCallback } from 'react';
import { InfiniteLoaderList } from '@/components/compose/infinite.loader.list';

import { DataQueryPaginateDto, MongoQueryTypesEnum, smartDate, translateSpecializations, User, UserTypeEnum, Visit, VisitStatusEnum } from '@matap/shared';
import { AppListRow } from '@/components/compose/app.list.row';
import styled from 'styled-components/native';
import { Box } from '@/components/base/box';
import { AppTextView } from '@/components/base/app.text.view';
import { useUser } from '@/hooks/use.user';
import { AppPressable } from '@/components/base/app.pressable';
import { useAssets } from '@/hooks/use.assets';
import { useQueryVisits } from '@/apis/visit';
import { hpVal } from '../../../utils';
import { ListRenderItemInfo } from 'react-native';
import { useAppRouter } from '@/hooks/use.app.router';
import { ModalNamesEnum } from '@/constants/enums/modal.names.enum';

interface Props {
  userId?: string;
}
function BaseVisitsHistoryView({ userId }: Props) {
  const router = useAppRouter();
  const historyApi = useQueryVisits<DataQueryPaginateDto<Visit>>({
    query: {
      or: userId
        ? [
            { status: VisitStatusEnum.ENDED, patientId: userId },
            { status: VisitStatusEnum.ENDED, doctorId: userId },
          ]
        : [],
      and: userId ? [] : [{ status: VisitStatusEnum.ENDED }],
      type: MongoQueryTypesEnum.PAGINATE,
      populate: ['doctor', 'patient', 'doctor.imageFile', 'patient.imageFile', 'doctor.doctorProfile.specializations'],
    },
  });

  const renderVisit = useCallback(
    ({ item }: ListRenderItemInfo<Visit>) => <VisitItem item={item} onPress={() => router.navigateTo(ModalNamesEnum.ChatsHistory, { visitId: item._id })} />,
    [router],
  );

  return (
    <Container>
      <ListContainer>
        <InfiniteLoaderList api={historyApi as any} render={renderVisit} />
      </ListContainer>
    </Container>
  );
}

const VisitItem = React.memo(function VisitItem({ item, onPress }: { item: Visit; onPress: (id: string) => void }) {
  const { userType } = useUser();
  const assets = useAssets();
  const targetUser: User = userType === UserTypeEnum.DOCTOR ? item.patient || { name: 'بیمار حذف شده است' } : item.doctor || ({ name: 'پزشک حذف شده است' } as any);
  return (
    <AppPressable onPress={() => onPress(item._id)}>
      <VisitColumn>
        <AppListRow
          imageUrl={targetUser.imageFile?.metadata.url || assets.images.default_blank_image}
          title={targetUser.name}
          subtitle={targetUser.type === UserTypeEnum.DOCTOR ? translateSpecializations(targetUser.doctorProfile?.specializations) : ''}
        >
          <DateRow fullWidth>
            <AppTextView text={smartDate(item.startedAt).formatJalali()} textColor={'black_light'} variant={'medium'} />
          </DateRow>
        </AppListRow>
      </VisitColumn>
    </AppPressable>
  );
});

const VisitColumn = styled(Box)`
  flex-direction: column;
`;

const DateRow = styled(Box)`
  background-color: ${({ theme }) => theme.colors.greyWhite};
  align-items: flex-start;
  padding: 5px;
  padding-left: 15px;
  border-bottom-right-radius: ${hpVal(1.2)};
  border-bottom-left-radius: ${hpVal(1.2)};
`;

const Container = styled(Box)`
  flex: 1;
`;

const ListContainer = styled(Box)`
  flex: 1;
  background-color: white;
  width: 100%;
  border-top-left-radius: ${({ theme }) => theme.hp(3)}px;
  border-top-right-radius: ${({ theme }) => theme.hp(3)}px;
  align-items: center;
`;

export const VisitsHistoryView = React.memo(BaseVisitsHistoryView);
